import { useState } from "react";
import { createPortal } from "react-dom";
import { X, ShoppingBag, Factory, Palette, Lock, ShieldCheck, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { API } from "@/lib/api";
import { COUNTRIES, detectCountry, dialFor } from "@/lib/countries";
import axios from "axios";

const DEFAULT_ISO = () => detectCountry() || "PH";

const CUSTOMER_ROLES = [
  { id: "buyer", label: "Buyer", icon: ShoppingBag },
  { id: "manufacturer", label: "Manufacturer", icon: Factory },
  { id: "creative", label: "Creative", icon: Palette },
];

const fmtErr = (d) =>
  d == null ? "Something went wrong. Please try again."
  : typeof d === "string" ? d
  : Array.isArray(d) ? d.map((e) => e?.msg || "").filter(Boolean).join(" ")
  : d?.msg || String(d);

// Two-step sign-in: (1) pick role, (2) Google or email+password (sign in / create account).
export const useSignInModal = () => {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(null);
  const [mode, setMode] = useState("signin"); // signin | register
  const [form, setForm] = useState({ name: "", email: "", password: "", countryIso: DEFAULT_ISO(), mobile: "", subscribe: true });
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, setUser } = useAuth();

  const reset = () => {
    setRole(null); setMode("signin");
    setForm({ name: "", email: "", password: "", countryIso: DEFAULT_ISO(), mobile: "", subscribe: true });
    setErr(""); setBusy(false); setShowPw(false);
  };

  const pick = (r) => {
    if (r === "admin") {
      setRole("admin");
      return;
    }
    setRole(r);
  };

  const googleSignIn = () => {
    try { localStorage.setItem("rada_role", role); } catch {}
    setOpen(false);
    login();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const url = mode === "register" ? `${API}/auth/register` : `${API}/auth/login`;
      const payload = mode === "register"
        ? {
            email: form.email, password: form.password, name: form.name, role,
            country_code: dialFor(form.countryIso), mobile: form.mobile,
            country: form.countryIso, subscribe: !!form.subscribe,
          }
        : { email: form.email, password: form.password };
      const { data } = await axios.post(url, payload, { withCredentials: true });
      if (data?.session_token) localStorage.setItem("rada_session", data.session_token);
      try { localStorage.setItem("rada_role", role); } catch {}
      setUser(data);
      setOpen(false);
      reset();
      window.location.href = "/account";
    } catch (e2) {
      setErr(fmtErr(e2.response?.data?.detail) || "Authentication failed");
      setBusy(false);
    }
  };

  const Modal = (
    <>{open ? createPortal(
      <div
        data-testid="signin-modal"
        className="fixed inset-0 z-[90] grid place-items-center bg-[#1F3864]/70 backdrop-blur-sm p-4"
        onClick={() => { setOpen(false); reset(); }}
      >
        <div
          className="rounded-2xl w-full max-w-md p-7 bg-[#0f1d38] border border-[#EBE6E0]/15 text-[#EBE6E0]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display font-bold text-2xl">
              {role && role !== "admin" ? `${role.charAt(0).toUpperCase() + role.slice(1)} sign in` : "Sign in to Rada Collab"}
            </h3>
            <button onClick={() => { setOpen(false); reset(); }} aria-label="Close" className="text-[#EBE6E0]/50 hover:text-[#EBE6E0]"><X size={20} /></button>
          </div>

          {!role ? (
            <>
              <p className="text-sm text-[#EBE6E0]/55 mb-6">Who are you signing in as?</p>
              <div className="space-y-2.5">
                {CUSTOMER_ROLES.map((r) => (
                  <button
                    key={r.id}
                    data-testid={`signin-role-${r.id}`}
                    onClick={() => pick(r.id)}
                    className="w-full flex items-center gap-3.5 rounded-xl border border-[#EBE6E0]/15 bg-[#EBE6E0]/5 px-4 py-3.5 text-left font-bold hover:border-[#D7E85C] hover:bg-[#D7E85C]/10 transition-colors"
                  >
                    <r.icon size={18} className="text-[#D7E85C] shrink-0" />
                    {r.label}
                  </button>
                ))}
              </div>
              <button
                data-testid="signin-role-admin"
                onClick={() => pick("admin")}
                className="mt-6 block mx-auto text-[11px] text-[#EBE6E0]/30 hover:text-[#EBE6E0]/60 underline-offset-2 hover:underline"
              >
                Admin
              </button>
            </>
          ) : role === "admin" ? (
            <div className="text-center py-4">
              <p data-testid="admin-restricted" className="font-display font-bold text-xl text-[#FF8473]">Restricted. Admin only.</p>
              <div className="mt-8 flex items-center justify-center gap-6">
                <button
                  data-testid="admin-back"
                  onClick={() => setRole(null)}
                  aria-label="Back"
                  className="grid place-items-center h-16 w-16 rounded-full bg-[#D7E85C] text-[#1F3864] hover:bg-[#EBE6E0] active:scale-95 transition-[background,transform]"
                >
                  <ArrowLeft size={26} />
                </button>
                <button
                  data-testid="admin-proceed"
                  onClick={() => window.open("/admin", "_blank", "noopener")}
                  className="text-[11px] text-[#EBE6E0]/30 hover:text-[#EBE6E0]/60 underline-offset-2 hover:underline"
                >
                  PROCEED
                </button>
              </div>
            </div>
          ) : (
            <>
              <button onClick={() => setRole(null)} className="text-xs text-[#EBE6E0]/50 hover:text-[#EBE6E0] mb-5">← Back</button>

              {/* Google */}
              <button
                data-testid="signin-google"
                onClick={googleSignIn}
                className="w-full flex items-center justify-center gap-2.5 bg-white text-[#1F3864] font-bold py-3 rounded-full hover:bg-[#EBE6E0] transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden><path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.3h3a9 9 0 0 0 2.6-6.6z"/><path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.3c-.8.6-1.9.9-3 .9-2.3 0-4.3-1.6-5-3.7H1v2.4A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M4 10.7a5.4 5.4 0 0 1 0-3.4V4.9H1a9 9 0 0 0 0 8.2l3-2.4z"/><path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.4 1.3L15 2.3A9 9 0 0 0 1 4.9l3 2.4c.7-2.1 2.7-3.7 5-3.7z"/></svg>
                Continue with Google
              </button>
              <p className="text-center text-[11px] text-[#EBE6E0]/40 mt-2">Gmail · Yahoo · Outlook and more</p>

              <div className="flex items-center gap-3 my-4">
                <span className="h-px flex-1 bg-[#EBE6E0]/15" />
                <span className="text-xs text-[#EBE6E0]/40">or</span>
                <span className="h-px flex-1 bg-[#EBE6E0]/15" />
              </div>

              {/* Email + password */}
              <form onSubmit={submit} className="space-y-2.5">
                {mode === "register" && (
                  <input data-testid="signin-name" required placeholder="Your name" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full bg-[#0a1526] border border-[#EBE6E0]/20 rounded-lg px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
                )}
                <input data-testid="signin-email" required type="email" placeholder="you@company.com" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-[#0a1526] border border-[#EBE6E0]/20 rounded-lg px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
                {mode === "register" && (
                  <>
                    <div className="flex gap-2">
                      <select data-testid="signin-country-code" value={form.countryIso}
                        onChange={(e) => setForm({ ...form, countryIso: e.target.value })}
                        className="max-w-[46%] bg-[#0a1526] border border-[#EBE6E0]/20 rounded-lg px-2.5 py-2.5 text-sm text-[#EBE6E0] outline-none focus:border-[#D7E85C]">
                        {COUNTRIES.map((c) => (
                          <option key={c.iso} value={c.iso}>{c.name} ({c.dial})</option>
                        ))}
                      </select>
                      <input data-testid="signin-mobile" type="tel" placeholder="Mobile number" value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                        className="flex-1 bg-[#0a1526] border border-[#EBE6E0]/20 rounded-lg px-3.5 py-2.5 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
                    </div>
                    <label data-testid="signin-subscribe-label" className="flex items-center gap-2.5 text-xs text-[#EBE6E0]/70 cursor-pointer py-1">
                      <input data-testid="signin-subscribe" type="checkbox" checked={form.subscribe}
                        onChange={(e) => setForm({ ...form, subscribe: e.target.checked })}
                        className="h-4 w-4 accent-[#D7E85C]" />
                      Subscribe me to email updates
                    </label>
                  </>
                )}
                <div className="relative">
                  <input data-testid="signin-password" required type={showPw ? "text" : "password"} placeholder={mode === "register" ? "Create a password (6+ chars)" : "Password"} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full bg-[#0a1526] border border-[#EBE6E0]/20 rounded-lg px-3.5 py-2.5 pr-16 text-sm text-[#EBE6E0] placeholder:text-[#EBE6E0]/40 outline-none focus:border-[#D7E85C]" />
                  <Lock size={15} className="absolute right-10 top-1/2 -translate-y-1/2 text-[#EBE6E0]/40 pointer-events-none" />
                  <button type="button" data-testid="signin-toggle-pw" aria-label="Toggle password visibility"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#EBE6E0]/50 hover:text-[#D7E85C]">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {err && <p data-testid="signin-error" className="text-xs font-bold text-[#FF8473]">{err}</p>}
                <button data-testid="signin-submit" disabled={busy}
                  className="w-full bg-[#D7E85C] text-[#1F3864] font-bold py-3 rounded-full hover:bg-[#EBE6E0] transition-colors disabled:opacity-60">
                  {busy ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
                </button>
              </form>
              <button data-testid="signin-toggle-mode" onClick={() => { setMode(mode === "signin" ? "register" : "signin"); setErr(""); }}
                className="mt-3 text-xs text-[#D7E85C] hover:underline">
                {mode === "signin" ? "New here? Create an account" : "Already have an account? Sign in"}
              </button>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-[#EBE6E0]/40">
                <ShieldCheck size={13} className="text-[#D7E85C]" /> Secure &amp; encrypted sign-in
              </p>
            </>
          )}
        </div>
      </div>,
      document.body
    ) : null}</>
  );

  return { openSignIn: () => { reset(); setOpen(true); }, SignInModal: Modal };
};
